(() => {
  'use strict';

  const DEFAULT_GIFT_THRESHOLD_CENTS = 30000000;
  const DEFAULT_SHIPPING_THRESHOLD_CENTS = 25000000;
  const GIFT_MESSAGE = 'Cortesía EVANO por completar la meta del carrito';
  const GIFT_PROPERTY_KEY = '_evano_gift';
  const GIFT_ACTIVATING_MESSAGE = 'Agregando tu Essence Mist…';
  const PREFLIGHT_TIMEOUT_MS = 8000;
  const GIFT_CHECKOUT_TIMEOUT_MS = 8000;
  const PRODUCT_REQUEST_TIMEOUT_MS = 1500;
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;
  const CHECKOUT_LINK_PATTERN = /\/checkout(?:\?|$)/;
  const nativeFetch = window.fetch.bind(window);
  const shopRoot = window.Shopify?.routes?.root || '/';

  let reconcilePromise = null;
  let cartMutationQueue = Promise.resolve();
  let pending = false;
  let scheduledTimer = null;
  let checkoutInProgress = false;
  let giftVariantId = 0;
  let giftHandle = '';
  let giftThresholdCents = DEFAULT_GIFT_THRESHOLD_CENTS;
  let shippingThresholdCents = DEFAULT_SHIPPING_THRESHOLD_CENTS;
  let giftConfigured = false;
  let giftUnavailable = true;
  let giftMisconfigured = false;
  let giftInventoryUnavailable = false;
  let toastsEnabled = false;
  let scheduledSource = 'startup';
  let previousTier = null;
  let incentiveViewed = false;
  let activeToast = null;
  let toastTimer = null;
  let unavailableReported = false;
  let mutationSyncTimer = null;
  const checkoutControlStates = new WeakMap();
  const shippingCopyHtml = new WeakMap();
  const preparedMistDialogs = new WeakSet();
  let activeMistTrigger = null;
  let suppressMistEscapeKeyup = false;

  const pushCroEvent = (event, detail = {}) => {
    const payload = { event, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('evano:cro-event', {
      detail: payload
    }));
  };

  const readIncentiveConfig = () => {
    const elements = [...document.querySelectorAll('[data-evano-cart-incentives]')];
    const element = elements.find((candidate) => candidate.dataset.enabled === 'true');
    if (!element) {
      giftConfigured = false;
      giftUnavailable = true;
      return null;
    }

    giftVariantId = Number(element.dataset.giftVariantId) || 0;
    giftHandle = element.dataset.giftHandle || '';
    giftThresholdCents =
      Number(element.dataset.giftThreshold) || DEFAULT_GIFT_THRESHOLD_CENTS;
    shippingThresholdCents =
      Number(element.dataset.shippingThreshold) || DEFAULT_SHIPPING_THRESHOLD_CENTS;
    toastsEnabled = element.dataset.toastsEnabled === 'true';
    giftConfigured = giftVariantId > 0 && giftHandle.length > 0;
    giftUnavailable =
      !giftConfigured ||
      element.dataset.giftAvailable !== 'true' ||
      giftMisconfigured ||
      giftInventoryUnavailable;
    if (!giftUnavailable) unavailableReported = false;
    return element;
  };

  const tierForCart = (cart) => {
    const subtotal = eligibleSubtotal(cart);
    if (!giftUnavailable && subtotal >= giftThresholdCents) return 2;
    if (subtotal >= shippingThresholdCents) return 1;
    return 0;
  };

  const formatCop = (cents) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(Math.max(0, cents) / 100);

  const incentiveMessageForSubtotal = (subtotal, giftVisible) => {
    if (subtotal < shippingThresholdCents) {
      return `Faltan ${formatCop(
        shippingThresholdCents - subtotal
      )} para envío gratis`;
    }
    if (giftVisible && subtotal < giftThresholdCents) {
      return `Faltan ${formatCop(
        giftThresholdCents - subtotal
      )} para tu Essence Mist`;
    }
    if (giftVisible) {
      return 'Tu Essence Mist ya está incluida';
    }
    return 'Tu envío gratis está activo';
  };

  const celebrateShippingUnlock = () => {
    document.querySelectorAll('[data-evano-cart-incentives]').forEach((element) => {
      element.classList.remove('is-celebrating-shipping');
      void element.offsetWidth;
      element.classList.add('is-celebrating-shipping');
      window.setTimeout(
        () => element.classList.remove('is-celebrating-shipping'),
        720
      );
    });
    document.querySelectorAll('[data-evano-shipping-benefit-status]').forEach((element) => {
      element.classList.remove('is-celebrating');
      void element.offsetWidth;
      element.classList.add('is-celebrating');
      window.setTimeout(() => element.classList.remove('is-celebrating'), 480);
    });
  };

  const ensureShippingBenefitStatus = () => {
    document
      .querySelectorAll('#t4s-mini_cart .evano-mini-cart-trust')
      .forEach((trust) => {
        trust.dataset.evanoMiniCartTrust = '';
        const shippingItem = trust.firstElementChild;
        if (shippingItem) shippingItem.dataset.evanoShippingTrustItem = '';

        let status = trust.previousElementSibling;
        if (!status?.matches('[data-evano-shipping-benefit-status]')) {
          status = document.createElement('p');
          status.className = 'evano-mini-cart-shipping-status';
          status.dataset.evanoShippingBenefitStatus = '';
          status.hidden = true;
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          status.innerHTML =
            '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M3 6h11v10H3zM14 9h3l4 4v3h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>' +
            '<span>Envío gratis activado</span>' +
            '<svg class="evano-mini-cart-shipping-status__check" aria-hidden="true" viewBox="0 0 24 24"><path d="m6 12 4 4 8-9"/></svg>';
          trust.parentNode.insertBefore(status, trust);
        }
      });
  };

  const syncShippingBenefitStatus = (subtotal) => {
    const shippingUnlocked = subtotal >= shippingThresholdCents;
    ensureShippingBenefitStatus();

    document
      .querySelectorAll('#t4s-mini_cart [data-evano-shipping-total]')
      .forEach((element) => {
        const amount = element.querySelector('[data-evano-shipping-total-amount]');
        const help = element.querySelector('[data-evano-shipping-total-help]');
        if (!amount || !help) return;

        if (shippingUnlocked) {
          amount.textContent = `${formatCop(subtotal)} COP`;
          amount.setAttribute('aria-label', `Total estimado ${formatCop(subtotal)} COP`);
          help.textContent = 'Envío gratis aplicado.';
          return;
        }

        const estimatedMinimum = formatCop(subtotal + 1000000);
        const estimatedMaximum = formatCop(subtotal + 1500000).replace(/^\D+/, '');
        amount.textContent = `${estimatedMinimum}–${estimatedMaximum} COP`;
        amount.setAttribute(
          'aria-label',
          `Total estimado entre ${estimatedMinimum} y ${formatCop(subtotal + 1500000)} COP`
        );
        help.textContent = 'El envío se confirma según tu ciudad.';
      });

    // The cart page summary survives Ajax line-item updates. Keep its copy in
    // sync even when the page was initially rendered above the shipping goal.
    document.querySelectorAll('[data-evano-cart-page-shipping-note]').forEach((element) => {
      const threshold = Number(element.dataset.shippingThreshold);
      const unlocked = threshold > 0 && subtotal >= threshold;
      const copy = unlocked
        ? element.dataset.shippingUnlockedCopy
        : element.dataset.shippingLockedCopy;
      if (copy && element.textContent.trim() !== copy) element.textContent = copy;
    });

    document.querySelectorAll('#t4s-mini_cart [data-evano-shipping-copy]').forEach((element) => {
      if (!shippingCopyHtml.has(element)) shippingCopyHtml.set(element, element.innerHTML);
      if (shippingUnlocked) {
        element.hidden = true;
      } else {
        element.hidden = false;
        element.innerHTML = shippingCopyHtml.get(element);
      }
      element.classList.toggle('is-unlocked', shippingUnlocked);
    });

    document.querySelectorAll('[data-evano-shipping-benefit-status]').forEach((element) => {
      element.hidden = !shippingUnlocked;
      element.classList.toggle('is-unlocked', shippingUnlocked);
    });

    document.querySelectorAll('[data-evano-mini-cart-trust]').forEach((element) => {
      element.classList.toggle('has-shipping-unlocked', shippingUnlocked);
      const shippingItem = element.querySelector('[data-evano-shipping-trust-item]');
      if (shippingItem) shippingItem.hidden = shippingUnlocked;
    });
  };

  const syncIncentiveUi = (cart) => {
    const subtotal = eligibleSubtotal(cart);
    const giftVisible = giftConfigured && !giftUnavailable;
    const giftPresent = giftLines(cart).some(
      (line) => Number(line.final_line_price) === 0 && Number(line.quantity) > 0
    );
    const giftActivating =
      giftVisible && subtotal >= giftThresholdCents && !giftPresent;
    const maximum = giftVisible ? giftThresholdCents : shippingThresholdCents;
    const shippingMarkerPercent = giftVisible ? 65 : 100;
    const giftStageRange = Math.max(1, giftThresholdCents - shippingThresholdCents);
    const rawProgressPercent = giftVisible
      ? subtotal < shippingThresholdCents
        ? (subtotal / shippingThresholdCents) * shippingMarkerPercent
        : shippingMarkerPercent +
          ((subtotal - shippingThresholdCents) / giftStageRange) *
            (100 - shippingMarkerPercent)
      : (subtotal / shippingThresholdCents) * 100;
    const progressPercent = Math.min(100, Math.max(0, rawProgressPercent));
    const shippingTruckPercent = progressPercent;
    const message = giftActivating
      ? GIFT_ACTIVATING_MESSAGE
      : incentiveMessageForSubtotal(subtotal, giftVisible);

    document.querySelectorAll('[data-evano-cart-incentives]').forEach((element) => {
      element.hidden = !cart.item_count;
      element.dataset.eligibleSubtotal = String(subtotal);
      element.dataset.giftPresent = String(giftPresent);
      element.dataset.giftActivating = String(giftActivating);
      element.classList.toggle(
        'is-shipping-unlocked',
        subtotal >= shippingThresholdCents
      );
      element.classList.toggle(
        'is-gift-unlocked',
        giftVisible && subtotal >= giftThresholdCents
      );

      const messageElement = element.querySelector('[data-evano-incentive-message]');
      if (messageElement) {
        messageElement.textContent = message;
        if (giftActivating) messageElement.setAttribute('aria-busy', 'true');
        else messageElement.removeAttribute('aria-busy');
      }

      const progress = element.querySelector('[role="progressbar"]');
      if (progress) {
        progress.setAttribute('aria-valuemax', String(maximum));
        progress.setAttribute('aria-valuenow', String(Math.min(subtotal, maximum)));
        progress.setAttribute('aria-valuetext', message);
      }

      const fill = element.querySelector('.evano-cart-incentives__fill');
      if (fill) fill.style.width = `${progressPercent}%`;

      const shippingTruck = element.querySelector('[data-evano-shipping-truck]');
      if (shippingTruck) {
        shippingTruck.style.left = `${shippingTruckPercent}%`;
        shippingTruck.classList.toggle(
          'is-unlocked',
          subtotal >= shippingThresholdCents
        );
      }

      const giftMarker = element.querySelector(
        '.evano-cart-incentives__marker--gift'
      );
      if (giftMarker) {
        giftMarker.hidden = !giftVisible;
        giftMarker.classList.toggle('is-unlocked', subtotal >= giftThresholdCents);
      }

      const shippingMilestone = element.querySelector(
        '.evano-cart-incentives__milestone--shipping'
      );
      if (shippingMilestone) {
        shippingMilestone.style.left = `${shippingMarkerPercent}%`;
        shippingMilestone.classList.toggle(
          'is-unlocked',
          subtotal >= shippingThresholdCents
        );
      }

      const giftMilestone = element.querySelector(
        '.evano-cart-incentives__milestones > li.is-gift'
      );
      if (giftMilestone) {
        giftMilestone.hidden = !giftVisible;
        giftMilestone.classList.toggle('is-unlocked', subtotal >= giftThresholdCents);
      }
    });

    syncShippingBenefitStatus(subtotal);
  };

  const showGiftActivationFromRenderedCart = () => {
    document.querySelectorAll('[data-evano-cart-incentives]').forEach((element) => {
      const shouldActivate =
        element.dataset.enabled === 'true' &&
        element.dataset.giftAvailable === 'true' &&
        element.dataset.giftPresent !== 'true' &&
        Number(element.dataset.eligibleSubtotal || 0) >=
          Number(element.dataset.giftThreshold || giftThresholdCents);
      if (!shouldActivate) return;

      element.dataset.giftActivating = 'true';
      const messageElement = element.querySelector('[data-evano-incentive-message]');
      if (messageElement) {
        messageElement.textContent = GIFT_ACTIVATING_MESSAGE;
        messageElement.setAttribute('aria-busy', 'true');
      }
      element
        .querySelector('[role="progressbar"]')
        ?.setAttribute('aria-valuetext', GIFT_ACTIVATING_MESSAGE);
    });
  };

  const removeToast = () => {
    window.clearTimeout(toastTimer);
    activeToast?.remove();
    activeToast = null;
  };

  const showToast = (message) => {
    if (!toastsEnabled || !message) return;
    removeToast();
    const toast = document.createElement('aside');
    toast.className = 'evano-incentive-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML =
      `<p class="evano-incentive-toast__copy"></p>` +
      `<button class="evano-incentive-toast__close" type="button" aria-label="Cerrar aviso">×</button>`;
    toast.querySelector('.evano-incentive-toast__copy').textContent = message;
    toast.querySelector('button').addEventListener('click', removeToast, { once: true });
    document.body.appendChild(toast);
    activeToast = toast;
    toastTimer = window.setTimeout(removeToast, 4500);
  };

  const observeIncentives = (cart, source = 'cart_update') => {
    const config = readIncentiveConfig();
    if (!giftConfigured || !cart) return;
    syncIncentiveUi(cart);
    if (!cart.item_count) return;
    const tier = tierForCart(cart);
    const subtotal = eligibleSubtotal(cart);
    const surface = config?.dataset.surface || 'cart';

    if (!incentiveViewed) {
      incentiveViewed = true;
      pushCroEvent('cart_incentive_viewed', {
        source,
        cart_surface: surface,
        incentive_tier: tier,
      });
    }

    if (previousTier !== null && tier > previousTier) {
      if (previousTier < 1 && tier >= 1) {
        pushCroEvent('shipping_unlocked', { source, cart_surface: surface });
        celebrateShippingUnlock();
        showToast('Envío gratis desbloqueado.');
      }
      if (previousTier < 2 && tier >= 2) {
        pushCroEvent('mist_unlocked', { source, cart_surface: surface });
        showToast('Essence Mist de cortesía desbloqueada.');
      }
    } else if (source === 'ajaxProduct:added' && tier === 0) {
      showToast(
        `Te faltan ${formatCop(shippingThresholdCents - subtotal)} para el envío gratis.`
      );
    }
    previousTier = tier;
  };

  const checkoutSurface = (control, form) => {
    const context = control?.closest?.('[data-cart-wrapper]') || form;
    return context?.closest?.('#t4s-mini_cart')
      ? 'mini_cart'
      : 'main_cart';
  };

  const checkoutAttempt = (control, form) => ({
    checkout_attempt_id:
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    checkout_surface: checkoutSurface(control, form),
    checkout_started_at: window.performance.now(),
  });

  const checkoutEventDetail = (attempt, detail = {}) => ({
    checkout_attempt_id: attempt.checkout_attempt_id,
    checkout_surface: attempt.checkout_surface,
    checkout_preflight_duration_ms: Math.max(
      0,
      Math.round(window.performance.now() - attempt.checkout_started_at)
    ),
    ...detail,
  });

  const withTimeout = (promise, timeoutMs, message) =>
    new Promise((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error(message)),
        timeoutMs
      );

      promise.then(
        (value) => {
          window.clearTimeout(timeout);
          resolve(value);
        },
        (error) => {
          window.clearTimeout(timeout);
          reject(error);
        }
      );
    });

  const setCheckoutControlBusy = (control, busy) => {
    if (!(control instanceof HTMLElement)) return;

    if (busy) {
      if (!checkoutControlStates.has(control)) {
        checkoutControlStates.set(control, {
          disabled: control.disabled,
          ariaDisabled: control.getAttribute('aria-disabled'),
          html: control.innerHTML,
          value:
            control instanceof HTMLInputElement ? control.value : undefined,
        });
      }

      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-disabled', 'true');
      if ('disabled' in control) control.disabled = true;
      if (control instanceof HTMLInputElement) {
        control.value = 'Verificando carrito…';
      } else {
        control.textContent = 'Verificando carrito…';
      }
      return;
    }

    const state = checkoutControlStates.get(control);
    control.removeAttribute('aria-busy');
    if (!state) return;

    if (state.ariaDisabled === null) control.removeAttribute('aria-disabled');
    else control.setAttribute('aria-disabled', state.ariaDisabled);
    if ('disabled' in control) control.disabled = state.disabled;
    if (control instanceof HTMLInputElement) control.value = state.value;
    else control.innerHTML = state.html;
    checkoutControlStates.delete(control);
  };

    const resetCheckoutNavigationState = () => {
          checkoutInProgress = false;
              document
                    .querySelectorAll(
                            'a[href*="/checkout"][aria-busy="true"], button[name="checkout"][aria-busy="true"], input[name="checkout"][aria-busy="true"]'
                                  )
                                        .forEach((control) => setCheckoutControlBusy(control, false));
                                          };
                                          
                                          const cartUrl = (path) => {
    const base = shopRoot.replace(/\/$/, '');
    return path === 'js' ? `${base}/cart.js` : `${base}/cart/${path}`;
                                                        };
                                                        
  const queueCartMutation = (request) => {
    const result = cartMutationQueue.then(request, request);
    cartMutationQueue = result.catch(() => {});
    return result;
  };
                                                                  
  const requestJson = async (path, options = {}) => {
    const requestUrl = cartUrl(path);
    const request = () => nativeFetch(requestUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body && !(options.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
      },
      cache: 'no-store',
      ...options,
    });
    const response = CART_MUTATION_PATTERN.test(requestUrl)
      ? await queueCartMutation(request)
      : await request();

    if (!response.ok) {
      const error = new Error(`Cart request failed (${response.status})`);
      error.status = response.status;
      throw error;
    }

    return response.json();
  };

  const isMistLine = (item) => Number(item.variant_id) === giftVariantId;

  const hasGiftProperty = (item) =>
    item?.properties?.[GIFT_PROPERTY_KEY] === 'true' ||
    item?.properties?.Obsequio === GIFT_MESSAGE;

  const eligibleSubtotal = (cart) =>
    cart.items.reduce((subtotal, item) => {
      if (isMistLine(item) && hasGiftProperty(item)) return subtotal;
      return subtotal + Number(item.final_line_price ?? item.line_price ?? 0);
    }, 0);

  const giftLines = (cart) =>
    giftVariantId > 0
      ? cart.items.filter((item) => isMistLine(item) && hasGiftProperty(item))
      : [];

  const freeMistLines = (cart) =>
    giftVariantId > 0
      ? cart.items.filter(
          (item) => isMistLine(item) && Number(item.final_line_price) === 0
        )
      : [];

  const validateCartState = (cart) => {
    if (giftUnavailable) return cart;

    const lines = giftLines(cart);
    const freeLines = freeMistLines(cart);
    const qualifies = eligibleSubtotal(cart) >= giftThresholdCents;
    const hasOneGift =
      freeLines.length === 1 && Number(freeLines[0]?.quantity) === 1;
    const hasPaidAutomaticGift = lines.some(
      (line) => Number(line.final_line_price) > 0
    );

    if (
      (qualifies && (!hasOneGift || hasPaidAutomaticGift)) ||
      (!qualifies && lines.length > 0)
    ) {
      throw new Error('Gift cart state could not be verified.');
    }

    return cart;
  };

  const notifyCartChanged = () => {
    const detail = { source: 'evano-free-gift' };
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    document.body.dispatchEvent(new CustomEvent('currency:update', { detail }));
  };

  const updateGiftLines = async (lines, keepOne) => {
    const updates = {};

    lines.forEach((line, index) => {
      updates[line.key] = keepOne && index === 0 ? 1 : 0;
    });

    await requestJson('update.js', {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  };

  const migrateGiftProperties = async (line) => {
    const properties = { ...(line.properties || {}) };
    delete properties._evano_free_gift;
    properties[GIFT_PROPERTY_KEY] = 'true';
    properties.Obsequio = GIFT_MESSAGE;

    await requestJson('change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: line.key,
        quantity: 1,
        properties,
      }),
    });
  };

  const reconcile = () => {
    // A new explicit retry must re-check a previously failed discount.
    if (!reconcilePromise) giftMisconfigured = false;
    readIncentiveConfig();
    if (!giftConfigured) return Promise.resolve(null);

    if (reconcilePromise) {
      pending = true;
      return reconcilePromise;
    }

    reconcilePromise = (async () => {
      try {
        let cart;
        let propertyMigrationAttempted = false;
        let giftAddedThisRun = false;

        do {
          pending = false;
          cart = await requestJson('js');
          const lines = giftLines(cart);
          const freeLines = freeMistLines(cart);
          const qualifies = eligibleSubtotal(cart) >= giftThresholdCents;

          if (!qualifies && lines.length > 0) {
            await updateGiftLines(lines, false);
            pushCroEvent('mist_removed_below_threshold', { source: scheduledSource });
            notifyCartChanged();
            pending = true;
            continue;
          }

          if (qualifies && giftHandle) {
            try {
              giftInventoryUnavailable = !(await giftVariantAvailableNow());
            } catch (error) {
              if (!unavailableReported) {
                unavailableReported = true;
                pushCroEvent('mist_inventory_check_failed', {
                  source: scheduledSource,
                });
              }
              // A failed request is not evidence that the gift is sold out.
              throw error;
            }
            giftUnavailable = giftMisconfigured || giftInventoryUnavailable;
          }

          if (giftUnavailable) {
            if (lines.length > 0) {
              await updateGiftLines(lines, false);
              notifyCartChanged();
            }
            if (
              !unavailableReported &&
              eligibleSubtotal(cart) >= giftThresholdCents
            ) {
              unavailableReported = true;
              pushCroEvent('mist_unavailable', { source: scheduledSource });
            }
            observeIncentives(cart, scheduledSource);
            return cart;
          }

          if (lines.some((line) => Number(line.final_line_price) > 0)) {
            await updateGiftLines(lines, false);
            giftMisconfigured = true;
            giftUnavailable = true;
            notifyCartChanged();
            pushCroEvent('mist_configuration_error', {
              source: scheduledSource,
              configuration_result: 'gift_not_free',
            });
            document.dispatchEvent(new CustomEvent('evano:free-gift-unavailable'));
            throw new Error('Gift discount could not be verified.');
          }

          let changed = false;

          if (qualifies && lines.length === 0 && freeLines.length === 0) {
            try {
              await requestJson('add.js', {
                method: 'POST',
                body: JSON.stringify({
                  items: [
                    {
                      id: giftVariantId,
                      quantity: 1,
                      properties: {
                        [GIFT_PROPERTY_KEY]: 'true',
                        Obsequio: GIFT_MESSAGE,
                      },
                    },
                  ],
                }),
              });
            } catch (error) {
              if (error.status === 422) {
                // Shopify can reject an add for reasons other than stock.
                giftInventoryUnavailable = !(await giftVariantAvailableNow());
                if (giftInventoryUnavailable) {
                  giftUnavailable = true;
                  unavailableReported = true;
                  pushCroEvent('mist_unavailable', { source: scheduledSource });
                  document.dispatchEvent(new CustomEvent('evano:free-gift-unavailable'));
                  return cart;
                }
              }
              throw error;
            }
            giftAddedThisRun = true;
            changed = true;
          } else if (
            qualifies &&
            (lines.length > 1 || Number(lines[0]?.quantity) !== 1)
          ) {
            await updateGiftLines(lines, true);
            pushCroEvent('mist_quantity_corrected', { source: scheduledSource });
            changed = true;
          } else if (
            qualifies &&
            lines.length === 1 &&
            lines[0]?.properties?.[GIFT_PROPERTY_KEY] !== 'true' &&
            !propertyMigrationAttempted
          ) {
            propertyMigrationAttempted = true;
            await migrateGiftProperties(lines[0]);
            changed = true;
          }

          if (changed) {
            notifyCartChanged();
            pending = true;
          }
        } while (pending);

        const validatedCart = validateCartState(cart);
        if (giftAddedThisRun) {
          pushCroEvent('mist_added', { source: scheduledSource });
        }
        observeIncentives(validatedCart, scheduledSource);
        return validatedCart;
      } catch (error) {
        console.warn('[EVANO free gift] Cart reconciliation failed.', error);
        throw error;
      } finally {
        reconcilePromise = null;
      }
    })();

    return reconcilePromise;
  };

  const scheduleReconcile = (delay = 700, source = 'cart_update') => {
    readIncentiveConfig();
    if (!giftConfigured) return;
    showGiftActivationFromRenderedCart();
    scheduledSource = source;
    window.clearTimeout(scheduledTimer);
    scheduledTimer = window.setTimeout(() => {
      reconcile().catch(() => {});
    }, delay);
  };

  const formHasUnsavedChanges = (form) =>
    [...form.elements].some((control) => {
      if (!control.name || control.disabled || ['checkout', 'update'].includes(control.name)) {
        return false;
      }
      if (control instanceof HTMLInputElement) {
        if (['checkbox', 'radio'].includes(control.type)) {
          return control.checked !== control.defaultChecked;
        }
        return control.value !== control.defaultValue;
      }
      if (control instanceof HTMLTextAreaElement) {
        return control.value !== control.defaultValue;
      }
      if (control instanceof HTMLSelectElement) {
        return [...control.options].some(
          (option) => option.selected !== option.defaultSelected
        );
      }
      return false;
    });

  const persistCartForm = async (form) => {
    if (!(form instanceof HTMLFormElement) || !formHasUnsavedChanges(form)) {
      return null;
    }

    const formData = new FormData(form);
    formData.delete('checkout');
    formData.delete('update');

    return requestJson('update.js', {
      method: 'POST',
      body: formData,
    });
  };

  const productUrl = (handle) => {
    const base = shopRoot.replace(/\/$/, '');
    return `${base}/products/${encodeURIComponent(handle)}.js`;
  };

  const fetchProduct = async (handle) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      PRODUCT_REQUEST_TIMEOUT_MS
    );

    try {
      const response = await nativeFetch(productUrl(handle), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Product request failed (${response.status})`);
      }

      return response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const giftVariantAvailableNow = async () => {
    if (!giftHandle || giftVariantId <= 0) return false;
    const product = await fetchProduct(giftHandle);
    const variant = product?.variants?.find(
      (candidate) => Number(candidate.id) === giftVariantId
    );
    return variant?.available === true;
  };

  const unavailableCartLines = async (cart) => {
    const sellableLines = cart.items.filter(
      (item) => Number(item.variant_id) !== giftVariantId && item.handle
    );
    const handles = [...new Set(sellableLines.map((item) => item.handle))];
    const products = await Promise.all(
      handles.map(async (handle) => [handle, await fetchProduct(handle)])
    );
    const productsByHandle = new Map(products);

    const unavailableLines = sellableLines.filter((item) => {
      const product = productsByHandle.get(item.handle);
      const variant = product?.variants?.find(
        (candidate) => Number(candidate.id) === Number(item.variant_id)
      );
      return variant ? variant.available === false : false;
    });

    const unavailableKeys = new Set(unavailableLines.map((item) => item.key));

    // cart/update.js does not validate inventory for variants already in the
    // cart. Re-submit each unchanged paid line through cart/change.js so
    // Shopify validates current availability and quantity before checkout.
    for (const item of sellableLines) {
      if (unavailableKeys.has(item.key)) continue;

      try {
        await requestJson('change.js', {
          method: 'POST',
          body: JSON.stringify({
            id: item.key,
            quantity: Number(item.quantity),
          }),
        });
      } catch (error) {
        if (error.status !== 422) throw error;
        unavailableLines.push(item);
        unavailableKeys.add(item.key);
      }
    }

    return unavailableLines;
  };

  const checkoutPreflight = async (persistedCart = null) => {
    const cart = persistedCart || (await requestJson('js'));
    const unavailableLines = await unavailableCartLines(cart);
    return { cart, unavailableLines };
  };

  const submitCheckout = (destination, form) => {
    if (!(form instanceof HTMLFormElement)) {
      window.location.assign(destination);
      return;
    }

    let checkoutInput = form.querySelector(
      'input[type="hidden"][name="checkout"][data-evano-checkout-submit]'
    );
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'checkout';
      checkoutInput.dataset.evanoCheckoutSubmit = '';
      form.append(checkoutInput);
    }

    HTMLFormElement.prototype.submit.call(form);
  };

  const removeCheckoutNotice = () => {
    document.querySelector('[data-evano-checkout-notice]')?.remove();
  };

  const checkoutNotice = ({ control, title, message, primary, secondary }) => {
    removeCheckoutNotice();

    const notice = document.createElement('section');
    notice.dataset.evanoCheckoutNotice = '';
    notice.setAttribute('role', 'alert');
    notice.setAttribute('aria-live', 'assertive');
    notice.tabIndex = -1;
    notice.style.cssText =
      'margin:0 0 12px;padding:16px;background:#fbf8f4;color:#191918;border-block:1px solid #d9d1c8;font-family:Montserrat,Arial,sans-serif;text-align:left';

    const heading = document.createElement('p');
    heading.style.cssText =
      'margin:0 0 4px;font-size:14px;font-weight:600;line-height:1.4';
    heading.textContent = title;
    notice.append(heading);

    if (message) {
      const copy = document.createElement('p');
      copy.style.cssText =
        'margin:0 0 12px;color:#625e58;font-size:13px;font-weight:400;line-height:1.5;overflow-wrap:anywhere';
      copy.textContent = message;
      notice.append(copy);
    }

    const actions = document.createElement('div');
    actions.style.cssText =
      'display:flex;flex-wrap:wrap;align-items:center;gap:8px 16px';

    if (primary) {
      const primaryButton = document.createElement('button');
      primaryButton.type = 'button';
      primaryButton.textContent = primary.label;
      primaryButton.style.cssText =
        'min-height:44px;padding:12px 16px;border:1px solid #191918;border-radius:2px;background:#191918;color:#fff;font:600 12px/1.25 Montserrat,Arial,sans-serif;letter-spacing:.08em;cursor:pointer';
      primaryButton.addEventListener('click', primary.action);
      actions.append(primaryButton);
    }

    if (secondary) {
      const secondaryButton = document.createElement('button');
      secondaryButton.type = 'button';
      secondaryButton.textContent = secondary.label;
      secondaryButton.style.cssText =
        'min-height:44px;padding:12px 0;border:0;border-bottom:1px solid #191918;background:transparent;color:#191918;font:500 14px/1.4 Montserrat,Arial,sans-serif;cursor:pointer';
      secondaryButton.addEventListener('click', secondary.action);
      actions.append(secondaryButton);
    }

    notice.append(actions);
    control?.parentElement?.insertBefore(notice, control);
    if (!notice.isConnected) document.body.append(notice);
    notice.focus({ preventScroll: true });
    return notice;
  };

  const removeUnavailableAndCheckout = async ({
    control,
    destination,
    form,
    unavailableLines,
    attempt,
  }) => {
    checkoutInProgress = true;
    setCheckoutControlBusy(control, true);

    try {
      const updates = Object.fromEntries(
        unavailableLines.map((line) => [line.key, 0])
      );
      const cart = await requestJson('update.js', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });

      pushCroEvent('checkout_inventory_removed', checkoutEventDetail(attempt, {
        unavailable_item_count: unavailableLines.length,
        checkout_result: 'inventory_removed',
      }));
      notifyCartChanged();

      const remainingItems = cart.items.filter(
        (item) => Number(item.variant_id) !== giftVariantId
      );
      if (remainingItems.length === 0) {
        checkoutInProgress = false;
        setCheckoutControlBusy(control, false);
        checkoutNotice({
          control,
          title: 'Tu carrito quedó vacío',
          message: 'La prenda agotada fue retirada. Puedes elegir otra disponible.',
          secondary: {
            label: 'Seguir comprando',
            action: () => window.location.assign('/collections/all'),
          },
        });
        return;
      }

      try {
        await withTimeout(
          reconcile(),
          GIFT_CHECKOUT_TIMEOUT_MS,
          'Gift reconciliation timed out.'
        );
      } catch (error) {
        console.warn('[EVANO checkout] Gift verification blocked checkout.', error);
        pushCroEvent(
          'checkout_gift_blocked',
          checkoutEventDetail(attempt, { checkout_result: 'gift_blocked' })
        );
        throw error;
      }

      submitCheckout(destination, form);
    } catch (error) {
      console.warn('[EVANO checkout] Unavailable item removal failed.', error);
      checkoutInProgress = false;
      setCheckoutControlBusy(control, false);
      checkoutNotice({
        control,
        title: 'No pudimos actualizar tu carrito',
        message: 'Revisa tu conexión e inténtalo de nuevo.',
        secondary: {
          label: 'Reintentar',
          action: () => {
            removeCheckoutNotice();
            removeUnavailableAndCheckout({
              control,
              destination,
              form,
              unavailableLines,
              attempt,
            });
          },
        },
      });
    }
  };

  const showUnavailableNotice = ({
    control,
    destination,
    form,
    unavailableLines,
    attempt,
  }) => {
    const productCount = new Set(unavailableLines.map((line) => line.product_id))
      .size;
    const productTitles = [
      ...new Set(
        unavailableLines.map(
          (line) => line.product_title || line.title || 'Producto agotado'
        )
      ),
    ];
    const listedProducts = productTitles.slice(0, 2).join(', ');
    const remainingProductCount = Math.max(productTitles.length - 2, 0);
    checkoutInProgress = false;
    setCheckoutControlBusy(control, false);
    pushCroEvent('checkout_inventory_blocked', checkoutEventDetail(attempt, {
      unavailable_item_count: unavailableLines.length,
      unavailable_product_count: productCount,
      checkout_result: 'inventory_blocked',
    }));

    checkoutNotice({
      control,
      title:
        productCount === 1
          ? 'Una prenda necesita ajuste'
          : `${productCount} prendas necesitan ajuste`,
      message: `Sin inventario suficiente: ${listedProducts}${
        remainingProductCount ? ` y ${remainingProductCount} más` : ''
      }. Revisa la cantidad o retírala antes de pagar.`,
      primary: {
        label: 'QUITAR Y CONTINUAR',
        action: () =>
          removeUnavailableAndCheckout({
            control,
            destination,
            form,
            unavailableLines,
            attempt,
          }),
      },
      secondary: {
        label: 'Revisar carrito',
        action: () => window.location.assign('/cart'),
      },
    });
  };

  const continueToCheckout = async (
    event,
    destination = '/checkout',
    form = null,
    control = null
  ) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutInProgress) return;

    checkoutInProgress = true;
    removeCheckoutNotice();
    setCheckoutControlBusy(control, true);
    const attempt = checkoutAttempt(control, form);
    pushCroEvent(
      'checkout_preflight_started',
      checkoutEventDetail(attempt, { checkout_result: 'started' })
    );

    try {
      const persistedCart = form ? await persistCartForm(form) : null;
      let preflight = null;

      try {
        preflight = await withTimeout(
          checkoutPreflight(persistedCart),
          PREFLIGHT_TIMEOUT_MS,
          'Checkout preflight timed out.'
        );
      } catch (error) {
        console.warn('[EVANO checkout] Inventory preflight blocked checkout.', error);
        pushCroEvent(
          'checkout_inventory_verification_blocked',
          checkoutEventDetail(attempt, {
            checkout_result: 'inventory_verification_blocked',
          })
        );
        throw error;
      }

      if (preflight?.unavailableLines.length) {
        showUnavailableNotice({
          control,
          destination,
          form,
          unavailableLines: preflight.unavailableLines,
          attempt,
        });
        return;
      }

      try {
        await withTimeout(
          reconcile(),
          GIFT_CHECKOUT_TIMEOUT_MS,
          'Gift reconciliation timed out.'
        );
      } catch (error) {
        console.warn('[EVANO checkout] Gift verification blocked checkout.', error);
        pushCroEvent(
          'checkout_gift_blocked',
          checkoutEventDetail(attempt, { checkout_result: 'gift_blocked' })
        );
        throw error;
      }

      pushCroEvent(
        'checkout_preflight_completed',
        checkoutEventDetail(attempt, {
          cart_item_count: preflight?.cart?.item_count ?? null,
          checkout_result: 'completed',
        })
      );
      submitCheckout(destination, form);
    } catch (error) {
      console.warn('[EVANO checkout] Checkout preparation failed.', error);
      checkoutInProgress = false;
      setCheckoutControlBusy(control, false);
      checkoutNotice({
        control,
        title: 'No pudimos verificar tu carrito y el obsequio',
        message: 'Tus productos siguen en el carrito. Reintenta para confirmar tus beneficios antes de pagar.',
        primary: {
          label: 'REINTENTAR',
          action: () => continueToCheckout({ preventDefault() {}, stopImmediatePropagation() {} }, destination, form, control),
        },
        secondary: {
          label: 'Revisar carrito',
          action: () => window.location.assign('/cart'),
        },
      });
    }
  };

  const observeCompletedCartMutation = () => {
    window.clearTimeout(mutationSyncTimer);
    mutationSyncTimer = window.setTimeout(() => {
      requestJson('js')
        .then((cart) => {
          readIncentiveConfig();
          syncIncentiveUi(cart);
          scheduleReconcile(0, 'cart_mutation_complete');
        })
        .catch((error) => {
          console.warn('[EVANO free gift] Cart mutation observer failed.', error);
        });
    }, 0);
  };

  const installFetchCartObserver = () => {
    if (!window.fetch || window.fetch.__evanoFreeGiftObserved) return;
    const originalFetch = window.fetch.bind(window);
    const observedFetch = (...args) => {
      const input = args[0];
      const options = args[1] || {};
      const method = String(options.method || input?.method || 'GET').toUpperCase();
      const urlValue =
        typeof input === 'string' || input instanceof URL ? String(input) : input?.url || '';
      const requestUrl = new URL(urlValue, window.location.origin);
      const cartMutation = `${requestUrl.pathname}${requestUrl.search}`;

      return originalFetch(...args).then((response) => {
        if (
          method !== 'GET' &&
          response.ok &&
          CART_MUTATION_PATTERN.test(cartMutation)
        ) {
          observeCompletedCartMutation();
        }
        return response;
      });
    };
    Object.defineProperty(observedFetch, '__evanoFreeGiftObserved', {
      value: true,
    });
    window.fetch = observedFetch;
  };

  const prepareMistDialog = (dialog) => {
    if (!dialog || preparedMistDialogs.has(dialog)) return;
    preparedMistDialogs.add(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      dialog.close();
    });

    dialog.addEventListener('close', () => {
      document.body.classList.toggle(
        'evano-mist-dialog-open',
        Boolean(document.querySelector('[data-evano-mist-info-dialog][open]'))
      );
      if (activeMistTrigger?.isConnected) {
        activeMistTrigger.setAttribute('aria-expanded', 'false');
        activeMistTrigger.focus({ preventScroll: true });
      }
      activeMistTrigger = null;
    });
  };

  const openMistDialog = (trigger) => {
    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog) return;

    document
      .querySelectorAll('[data-evano-mist-info-dialog][open]')
      .forEach((openDialog) => {
        if (openDialog !== dialog) openDialog.close();
      });

    prepareMistDialog(dialog);
    activeMistTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('evano-mist-dialog-open');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    pushCroEvent('mist_info_opened', {
      surface: trigger.closest('[data-evano-cart-incentives]')?.dataset.surface || 'cart',
    });
  };

  const addMistFromDialog = async (control) => {
    if (control.getAttribute('aria-busy') === 'true') return;

    const dialog = control.closest('[data-evano-mist-info-dialog]');
    const status = dialog?.querySelector('[data-evano-mist-add-status]');
    const label = control.querySelector('[data-evano-mist-add-label]');
    const variantId = Number(control.dataset.variantId) || 0;
    const surface = control.dataset.surface || 'cart';
    const originalLabel = label?.textContent || '';

    if (!dialog || variantId <= 0) return;

    control.disabled = true;
    control.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'AGREGANDO…';
    if (status) status.textContent = '';
    pushCroEvent('mist_info_add_started', {
      surface,
      variant_id: variantId,
      quantity: 1,
    });

    try {
      await requestJson('add.js', {
        method: 'POST',
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      if (label) label.textContent = 'AGREGADO';
      if (status) status.textContent = 'Essence Mist añadido al carrito.';
      pushCroEvent('mist_purchased_from_info', {
        surface,
        variant_id: variantId,
        quantity: 1,
      });
      notifyCartChanged();
      scheduleReconcile(0, 'mist_info_purchase');

      window.setTimeout(() => {
        if (dialog.isConnected && dialog.open) dialog.close();
      }, 240);
    } catch (error) {
      control.disabled = false;
      control.removeAttribute('aria-busy');
      if (label) label.textContent = originalLabel;
      if (status) {
        status.textContent =
          error.status === 422
            ? 'Este producto no está disponible en este momento.'
            : 'No pudimos añadirlo. Inténtalo de nuevo.';
      }
      pushCroEvent('mist_info_add_failed', {
        surface,
        variant_id: variantId,
        status: error.status || 'network_error',
      });
    }
  };

  const installJqueryCartObserver = () => {
    const jquery = window.jQuery_T4NT || window.jQuery;
    if (!jquery?.fn?.on) return false;

    jquery(document)
      .off('ajaxComplete.evanoFreeGift')
      .on('ajaxComplete.evanoFreeGift', (_event, xhr, settings = {}) => {
        const method = String(settings.type || settings.method || 'GET').toUpperCase();
        const requestUrl = new URL(settings.url || '', window.location.origin);
        const cartMutation = `${requestUrl.pathname}${requestUrl.search}`;
        const succeeded = Number(xhr?.status) >= 200 && Number(xhr?.status) < 300;
        if (method === 'GET' || !succeeded || !CART_MUTATION_PATTERN.test(cartMutation)) {
          return;
        }

        observeCompletedCartMutation();
      });

    return true;
  };

  const ensureJqueryCartObserver = (attempt = 0) => {
    if (installJqueryCartObserver() || attempt >= 20) return;
    window.setTimeout(() => ensureJqueryCartObserver(attempt + 1), 100);
  };

  installFetchCartObserver();

  document.addEventListener('click', (event) => {
    const addControl = event.target.closest?.('[data-evano-mist-add]');
    if (addControl) {
      event.preventDefault();
      addMistFromDialog(addControl).catch(() => {});
      return;
    }

    const productLink = event.target.closest?.('[data-evano-mist-product-link]');
    if (productLink) {
      pushCroEvent('mist_product_viewed_from_info', {
        surface: productLink.dataset.surface || 'cart',
      });
      return;
    }

    const closeControl = event.target.closest?.('[data-evano-mist-info-close]');
    if (closeControl) {
      closeControl.closest('[data-evano-mist-info-dialog]')?.close();
      return;
    }

    const trigger = event.target.closest?.('[data-evano-mist-info-open]');
    if (!trigger) return;
    event.preventDefault();
    openMistDialog(trigger);
  });

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const openDialog = document.querySelector(
        '[data-evano-mist-info-dialog][open]'
      );
      if (!openDialog) return;
      suppressMistEscapeKeyup = true;
      event.preventDefault();
      event.stopImmediatePropagation();
      openDialog.close();
    },
    true
  );

  window.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' || !suppressMistEscapeKeyup) return;
      suppressMistEscapeKeyup = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const checkoutControl = event.target.closest?.(
        'a[href], button[name="checkout"], input[name="checkout"]'
      );
      const checkoutLink = checkoutControl?.matches('a[href]')
        ? checkoutControl
        : null;

      if (checkoutLink && CHECKOUT_LINK_PATTERN.test(checkoutLink.href)) {
        continueToCheckout(event, checkoutLink.href, null, checkoutControl);
      } else if (checkoutControl?.name === 'checkout') {
        continueToCheckout(
          event,
          '/checkout',
          checkoutControl.form,
          checkoutControl
        );
      }
    }
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      const isCheckoutSubmit =
        submitter?.name === 'checkout' || submitter?.value === 'checkout';

      if (isCheckoutSubmit) {
        continueToCheckout(
          event,
          '/checkout',
          event.target,
          event.submitter
        );
      }
    },
    true
  );

  ['cart:refresh', 'cart:updated', 'cart:change', 'ajaxProduct:added'].forEach(
    (eventName) =>
      document.addEventListener(eventName, (event) => {
        if (event.detail?.source === 'evano-free-gift') return;
        scheduleReconcile(700, eventName);
      })
  );

  ensureJqueryCartObserver();

  window.addEventListener('pagehide', resetCheckoutNavigationState);
    window.addEventListener('pageshow', () => {
        resetCheckoutNavigationState();
            scheduleReconcile(0, 'pageshow');
              });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleReconcile(0, 'startup'), {
      once: true,
    });
  } else {
    scheduleReconcile(0, 'startup');
  }
})();
